import React from 'react'
import { FileUpload as FileUploadStyles, FileItem as FileItemStyles } from '@airbus/elements/elements'
import { CheckCircle, Clear, Error, Replay } from '@mui/icons-material'
import clsx from 'clsx'
import { createUseStyles } from '../../lib/styling'
import Button from '../Button'
import ProgressBar from '../ProgressBar'
import Typography from '../Typography'
import IconButton from '../IconButton'
import { CommonProps } from '../helperTypes'
import { FileItem } from './FileUpload'

const useStyles = createUseStyles(FileUploadStyles, 'fileupload-filelist')
const useFileStyles = createUseStyles(FileItemStyles, 'fileupload-fileitem')

type Props = {
  /** Called when user clicks on retry button, with the id of the file as its parameter */
  onRetry?: (id: number) => void
  /** When a file is deleted, onDelete function is called with the id of the deleted file as its parameter */
  onDelete: (id: number) => void
  /**
   * Array of objects containing the state of the upload progress and percentage.<br />
   * structure: { id: number, data: file, retry: boolean, errorMsg: string, status: success | uploading | error | added, percentage: number }
   */
  files: Array<FileItem>
}

export type FileListProps = Props & CommonProps

const FileList = (props: FileListProps) => {
  const { className, onRetry, onDelete, files, ...rest } = props
  const classes = useStyles()
  const fileClasses = useFileStyles()

  const renderFileProgress = (file: FileItem) => {
    const { percentage, status } = file
    if (status === 'uploading') {
      return <ProgressBar showPercentage percentageRight value={percentage} />
    } else if (status === 'success') {
      return (
        <div className={fileClasses.status}>
          <CheckCircle />
          Uploaded
        </div>
      )
    } else if (status === 'error') {
      return (
        <>
          {file.retry && (
            <div>
              <ProgressBar status='error' showPercentage percentageRight value={percentage} />
            </div>
          )}
          <div className={fileClasses.status}>
            <Error />
            {file.errorMsg}
          </div>
          {file.retry && onRetry && (
            <Button
              className={fileClasses.retryButton}
              icon={<Replay />}
              variant='ghost'
              size='small'
              onClick={() => onRetry(file.id)}
            >
              Retry
            </Button>
          )}
        </>
      )
    }
    return null
  }

  return (
    <div className={clsx(className, classes.files)} {...rest}>
      {files.map((file: FileItem) => (
        <div
          className={clsx(fileClasses.root, fileClasses[`_${file.status}`], file.retry && fileClasses.retry)}
          key={file.id}
        >
          <IconButton
            className={fileClasses.removeButton}
            variant='ghost'
            size='small'
            aria-label='Delete'
            onClick={() => onDelete && onDelete(file.id)}
          >
            <Clear />
          </IconButton>
          <Typography variant='medium' className={fileClasses.name}>
            {file.data.name}
          </Typography>
          {renderFileProgress(file)}
        </div>
      ))}
    </div>
  )
}

export default FileList
